/**
 * moduleRegistry.ts — registry of advanced placement / optimisation modules
 *
 * Each module is a "tick-of-the-box" enhancement. When enabled, the module
 * is active across the relevant downstream pages (Plume Coverage, Validation
 * Steps, Diffuser Comparison, etc.); when disabled, the page remains
 * available for reading but the logic is dormant.
 *
 * Toggle state is persisted to `rb_modules_enabled_v1`.
 */

import { useEffect, useState } from 'react';
import { loadPersisted, savePersisted, onPersistChange } from '@/lib/sessionPersistence';

export const MODULES_KEY = 'rb_modules_enabled_v1';

export type ModuleStatus = 'available' | 'coming_soon';
export type ModuleCategory = 'placement' | 'optimisation' | 'integration';

export interface OptModule {
  id: string;
  label: string;
  shortDesc: string;
  longDesc: string;
  ref: string;
  status: ModuleStatus;
  path?: string;
  category: ModuleCategory;
  iconName: string;          // lucide icon name (looked up at render)
  affects: string[];         // which pages this module modifies when enabled
  /** Mutually-exclusive modules — cannot be enabled together. */
  hardConflicts?: { id: string; reason: string }[];
  /** Modules with overlapping physics — both can be on but flagged. */
  softConflicts?: { id: string; reason: string }[];
}

export const MODULES: OptModule[] = [
  // ─── Available placement modules ───────────────────────────────────────────
  {
    id: 'asaeda_c',
    softConflicts: [
      { id: 'langmuir',
        reason: 'Asaeda-Imberger assumes stratified hypolimnion plumes detraining below the thermocline. ' +
                'Langmuir cells are a surface-mixed phenomenon driven by wind. The two regimes can co-exist in a ' +
                'real reservoir (stratified below, mixed above) but the diagnostics target different parts of ' +
                'the water column. Interpret C-criterion as bottom-half behaviour and Langmuir as top-1-m behaviour.' },
    ],
    label: 'Asaeda-Imberger C-criterion',
    shortDesc: 'Plume-merging threshold for grid spacing',
    longDesc:
      'Computes the dimensionless C = (M·N³)^(1/4) / B^(1/2) from Asaeda & Imberger 1993. ' +
      'C < 1.4 → adjacent plumes merge as one (Type-2 regime); C > 2.4 → plumes remain ' +
      'independent (Type-1 regime). Tells you whether the current array spacing produces ' +
      'distinct plumes or destructive merging.',
    ref: 'Asaeda & Imberger 1993; Schladow 1992',
    status: 'available',
    path: '/module/asaeda-imberger',
    category: 'placement',
    iconName: 'Activity',
    affects: ['Validation Steps L7', 'Plume Coverage'],
  },
  {
    id: 'deep_zone',
    softConflicts: [
      { id: 'dds_optimiser',
        reason: 'DDS searches the full unconstrained decision space and will not honour the deep-zone ' +
                'preference. Two options: (a) accept that DDS overrides deep-zone and treat the optimiser ' +
                'result as the final answer; (b) narrow the DDS search bounds so it only explores deep-zone ' +
                'positions. Soft conflict because the warnings can be reconciled.' },
      { id: 'nsga2',
        reason: 'NSGA-II also operates on the unconstrained decision space and ignores the deep-zone hint. ' +
                'The Pareto front it returns may include solutions with shallow-zone placements that the ' +
                'deep-zone module would reject. Document which logic takes precedence.' },
      { id: 'resusp_penalty',
        reason: 'Deep-zone positions often coincide with the most organic-rich sediment layers, which is ' +
                'exactly where the Resuspension penalty (Bryant 2011) flags risk of SOD uplift. Both modules ' +
                'tell complementary stories about the same placement — review both verdicts before committing.' },
    ],
    label: 'Deep-zone placement weighting',
    shortDesc: 'Prioritise modules in deepest 30 % of footprint',
    longDesc:
      'Imteaz & Asaeda 2000 showed that modules placed in the deepest 30 % of the ' +
      'reservoir footprint deliver up to 2× the destratification effect of modules in ' +
      'shallower zones at the same airflow. When enabled, the recommendation engine ' +
      'weights candidate positions toward the deep zone.',
    ref: 'Imteaz & Asaeda 2000; Gantzer et al. 2009',
    status: 'available',
    path: '/module/deep-zone-weighting',
    category: 'placement',
    iconName: 'Mountain',
    affects: ['Plume Coverage', 'Module Array'],
  },
  {
    id: 'wind_tilt',
    label: 'Wind-tilt plume correction',
    shortDesc: 'Surface-boil shift under prevailing wind',
    longDesc:
      'Lemckert 2008 measured persistent fountain-like upwelling above each module and ' +
      'its deflection under wind shear. When enabled, each module marker on the coverage ' +
      'map shows the wind-corrected surface boil location, which can shift by 0.3–1.2 m ' +
      'depending on wind speed and submergence.',
    ref: 'Lemckert 2008; Thorpe 2004',
    status: 'available',
    path: '/module/wind-tilt',
    category: 'placement',
    iconName: 'Wind',
    affects: ['Plume Coverage', 'Sampling Plan'],
  },
  {
    id: 'resusp_penalty',
    softConflicts: [
      { id: 'deep_zone',
        reason: 'Deep-zone placement (Imteaz 2000) prioritises the deepest sediments — usually the highest-' +
                'organic, lowest-density layers — which are exactly the zones Bryant 2011 flags as most likely ' +
                'to release additional SOD on disturbance. The two modules can both run but the user must ' +
                'decide whether the destratification benefit outweighs the resuspension risk.' },
    ],
    label: 'Resuspension penalty flag',
    shortDesc: 'Detect modules placed over organic-rich sub-surface sediment',
    longDesc:
      'Bryant et al. 2011 showed that wrongly placed modules can INCREASE sediment ' +
      'oxygen demand by 20–30 % because the plume uncovers organic-rich layers below ' +
      'a thin oxidised surface. When enabled, validation flags any module whose plume ' +
      'bed-shear exceeds the resuspension threshold for the chosen bed type, with ' +
      'guidance to relocate or reduce airflow.',
    ref: 'Bryant et al. 2011; Mehta 1986',
    status: 'available',
    path: '/module/resuspension-penalty',
    category: 'placement',
    iconName: 'AlertOctagon',
    affects: ['Validation Steps L6', 'Bed Assessment'],
  },
  {
    id: 'cost_per_o2',
    label: 'Cost-per-mg-O₂ optimisation',
    shortDesc: 'CAPEX + OPEX efficiency ranking',
    longDesc:
      'Bryant et al. 2011 / Gantzer et al. 2009 efficiency metric. Ranks each placement ' +
      'and each candidate diffuser by lifetime cost (£) per mg O₂ delivered. Often ' +
      'redistributing existing modules outperforms adding new ones. When enabled, ' +
      'diffuser comparison adds a new column and the comparison ranking is re-sorted.',
    ref: 'Bryant et al. 2011; Gantzer et al. 2009',
    status: 'available',
    path: '/module/cost-per-o2',
    category: 'optimisation',
    iconName: 'PoundSterling',
    affects: ['Diffuser Comparison', 'Master Report'],
  },

  // ─── Coming-soon placeholders ────────────────────────────────────────────
  {
    id: 'dds_optimiser',
    label: 'DDS pattern-search optimiser',
    shortDesc: 'Dynamically Dimensioned Search auto-placement',
    longDesc:
      'Tolson & Shoemaker 2007 algorithm. Takes a set of candidate module positions and ' +
      'iteratively converges on the Pareto-optimal subset that maximises DO recovery ' +
      'subject to bed-shear, AWWA and AOTR/SOD constraints. Faster than NSGA-II for ' +
      '20–50 candidate positions.',
    ref: 'Tolson & Shoemaker 2007',
    status: 'available',
    path: '/module/dds-optimiser',
    category: 'optimisation',
    iconName: 'Sparkles',
    affects: ['Module Array', 'Plume Coverage'],
    hardConflicts: [
      { id: 'nsga2',
        reason: 'DDS is a single-objective hill-climber (minimise lifetime £/kg O₂). NSGA-II is a multi-' +
                'objective evolutionary optimiser returning a Pareto front (cost vs anoxia). Running both on ' +
                'the same decision space produces two incompatible "optimal" placements that cannot both be ' +
                'applied. Pick whichever framework matches your decision philosophy.' },
    ],
    softConflicts: [
      { id: 'deep_zone',
        reason: 'DDS will ignore the deep-zone preference. Disable deep-zone or narrow the DDS search bounds.' },
    ],
  },
  {
    id: 'nsga2',
    label: 'NSGA-II multi-objective Pareto',
    shortDesc: 'Multi-objective evolutionary optimiser',
    longDesc:
      'Reed et al. 2003 NSGA-II implementation. Optimises placements under multiple ' +
      'objectives simultaneously: DO recovery, CAPEX, bed disturbance, methane emission. ' +
      'Returns a Pareto front from which the user picks the trade-off they prefer.',
    ref: 'Reed et al. 2003',
    status: 'available',
    path: '/module/nsga2',
    category: 'optimisation',
    iconName: 'GitBranch',
    affects: ['Module Array', 'GHG Balance'],
    hardConflicts: [
      { id: 'dds_optimiser',
        reason: 'NSGA-II is a multi-objective Pareto optimiser. DDS is a single-objective hill-climber. ' +
                'Both target the same decision space (module placement) but use incompatible solution ' +
                'concepts. Pick one and document the choice in the design submission.' },
    ],
    softConflicts: [
      { id: 'deep_zone',
        reason: 'NSGA-II searches the full unconstrained decision space and may return Pareto-optimal ' +
                'solutions with shallow-zone placements that the deep-zone module would reject.' },
    ],
  },
  {
    id: 'glm_coupling',
    label: 'GLM lake-physics coupling',
    shortDesc: 'Couple to the General Lake Model',
    longDesc:
      'Hipsey et al. 2014 General Lake Model. Imports measured bathymetry + module ' +
      'placements + climate forcing and returns the GLM-simulated stratification, DO ' +
      'profile and water-quality trajectory. Industry standard for limnological modelling.',
    ref: 'Hipsey et al. 2014',
    status: 'available',
    path: '/module/glm-coupling',
    category: 'integration',
    iconName: 'Layers',
    affects: ['Thermal Model', 'Algal Dynamics'],
    hardConflicts: [
      { id: 'elcom_coupling',
        reason: 'GLM is a one-dimensional vertical model that assumes horizontal homogeneity. ELCOM is a ' +
                'three-dimensional hydrodynamic model that resolves lateral flow and shoreline effects. ' +
                'Both produce hydrodynamic predictions for the same lake but they cannot be used ' +
                'simultaneously — their outputs would conflict. Use GLM for vertical structure (cheap, ' +
                'fast) or ELCOM for full 3-D flow patterns (expensive, slow). Not both.' },
    ],
  },
  {
    id: 'elcom_coupling',
    label: 'ELCOM 3-D hydrodynamic coupling',
    shortDesc: 'Couple to Estuary, Lake and Coastal Ocean Model',
    longDesc:
      'Hodges & Dallimore 2006 ELCOM. Industry-standard 3-D model with near-field bubble-' +
      'plume source representation. Lets you test module placements before deployment ' +
      'against full 3-D hydrodynamics.',
    ref: 'Hodges & Dallimore 2006',
    status: 'available',
    path: '/module/elcom-coupling',
    category: 'integration',
    iconName: 'Box',
    affects: ['Plume Coverage', 'Reservoir Layout'],
    hardConflicts: [
      { id: 'glm_coupling',
        reason: 'ELCOM is a 3-D model with explicit horizontal and vertical resolution. GLM is a 1-D ' +
                'vertical model assuming horizontal homogeneity. Both describe the same physical system ' +
                'with incompatible dimensionality. Run ELCOM when shoreline interaction, wind set-up, or ' +
                'lateral inflow matters; otherwise GLM is cheaper. Cannot run both at once.' },
    ],
  },
  // ─── Bubble physics — Coalescence (Kulkarni & Joshi 2005) ─────────────────
  {
    id: 'coalescence',
    label: 'Bubble swarm coalescence',
    shortDesc: 'Kulkarni–Joshi 2005 bubble-size correction in dense plumes',
    longDesc:
      'Geary & Rice 1991 / Kulkarni & Joshi 2005 swarm-coalescence model. Computes the effective ' +
      'bubble diameter after coalescence inside the rising plume: d_eff = d_Fritz · (1 + 0.4·ψ)^(1/3) ' +
      'where ψ = √(q_pore / (l_pore · v_rise)). Higher pore density and higher per-pore airflow ' +
      'increase coalescence, reducing the active mass-transfer area and lowering effective SOTE.',
    ref: 'Geary & Rice 1991; Kulkarni & Joshi 2005',
    status: 'available',
    path: '/module/coalescence',
    category: 'placement',
    iconName: 'Activity',
    affects: ['Bed Assessment §15.1', 'Diffuser Module', 'Validation L2 bubble'],
    softConflicts: [],
  },

  // ─── Bubble physics — Higbie penetration mass transfer (1935) ──────────────
  {
    id: 'higbie_kL',
    label: 'Higbie penetration kₗ',
    shortDesc: 'Surface-renewal mass-transfer coefficient',
    longDesc:
      'Higbie 1935 penetration theory for the liquid-side mass-transfer coefficient ' +
      'k_L = 2·√(D_O2 / (π · t_contact)). Alternative to the empirical Stenstrom & Rosso 2008 ' +
      'SOTE correlation. Useful as a cross-check on small-bubble (< 1 mm) regimes where the ' +
      'empirical correlation breaks down. Reports k_L (m/s), Sherwood number and the ratio of ' +
      'predicted to Frössling lower bound.',
    ref: 'Higbie 1935; Danckwerts 1951',
    status: 'available',
    path: '/module/higbie-kl',
    category: 'placement',
    iconName: 'Layers',
    affects: ['Bed Assessment §3', 'Bubble physics cross-check'],
    softConflicts: [],
  },

  // ─── Algae — Reynolds 2006 CSR functional groups ──────────────────────────
  {
    id: 'csr_groups',
    label: 'Reynolds CSR functional groups',
    shortDesc: 'Trait-based phytoplankton classification',
    longDesc:
      'Reynolds 2006 / Reynolds, Huszar et al. 2002 functional-group framework. Classifies ' +
      'phytoplankton into 38 codonominal groups by ecology (C — colonist-strategists, S — stress-' +
      'tolerant, R — disturbance-tolerant). Predicts the dominant FG given mixing energy, light, ' +
      'nutrients and temperature. Complements the existing 3-group (diatoms/greens/cyanobacteria) ' +
      'model — adds taxonomic resolution without replacing it.',
    ref: 'Reynolds 2006; Reynolds, Huszar et al. 2002',
    status: 'available',
    path: '/module/csr-groups',
    category: 'placement',
    iconName: 'FlaskConical',
    affects: ['Algal Dynamics — taxonomic resolution', 'WQ Predictor'],
    softConflicts: [],
  },

  // ─── Bed physics — Boudreau diagenesis (1997) ─────────────────────────────
  {
    id: 'boudreau_diagenesis',
    label: 'Boudreau 1-D pore-water diagenesis',
    shortDesc: 'Sediment pore-water O₂ profile + redox depths',
    longDesc:
      'Boudreau 1997 reaction-diffusion model for the sediment-water interface. Solves the 1-D ' +
      'steady-state pore-water O₂ profile O₂(z) = O₂(0)·exp(-z/L_O2) where L_O2 = √(D_eff/k). ' +
      'Reports the aerobic depth, the denitrification onset depth, and the Fe(III) reduction ' +
      'depth. Underpins the v47 Bed Assessment §11 calculation but exposes it as a standalone ' +
      'verifiable module.',
    ref: 'Boudreau 1997; Berner 1980; Rasmussen & Jørgensen 1992',
    status: 'available',
    path: '/module/boudreau-diagenesis',
    category: 'placement',
    iconName: 'Mountain',
    affects: ['Bed Assessment §11', 'Sediment Bed Physics', 'Validation L6 sediment'],
    softConflicts: [
      { id: 'resusp_penalty',
        reason: 'Boudreau predicts the aerobic crust thickness; Bryant resuspension predicts when plume ' +
                'shear breaks through that crust. They are complementary — use both together to size the ' +
                'crust depth (Boudreau) AND check the plume does not breach it (resuspension penalty).' },
    ],
  },

  // ─── Reservoir physics — Internal seiche mixing (Wüest et al. 1992) ───────
  {
    id: 'internal_seiche',
    label: 'Internal seiche & basin-scale mixing',
    shortDesc: 'Wüest internal-wave energy budget',
    longDesc:
      'Wüest et al. 1992 / Münnich 1992 internal-seiche framework. Computes the V1H1 seiche period ' +
      'T = 2L·√(ρ/(g·Δρ)) and the basin-scale mixing energy contribution from wind-driven internal ' +
      'wave breaking on the bed. Important for tall narrow basins; less so for wide flat basins like ' +
      'Barcombe but still useful as a sanity check on the Wedderburn number.',
    ref: 'Wüest et al. 1992; Münnich, Wüest & Imboden 1992',
    status: 'available',
    path: '/module/internal-seiche',
    category: 'placement',
    iconName: 'Activity',
    affects: ['Stratification analysis', 'Bed Assessment §12'],
    softConflicts: [
      { id: 'asaeda_c',
        reason: 'Both describe stratified-water phenomena. Asaeda-C addresses local plume-merging; ' +
                'internal seiche addresses basin-scale wave energy. Both can run together — interpret ' +
                'separately rather than averaging.' },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EAST-ASIAN literature modules — Japan & China peer-reviewed
  // ═══════════════════════════════════════════════════════════════════════════

  // ─── Japan: Cyano-stratification feedback (Kumagai et al. 2000, Lake Biwa) ─
  {
    id: 'kumagai_feedback',
    label: 'Cyano–stratification positive feedback',
    shortDesc: 'Kumagai 2000 self-reinforcing bloom-stratification loop',
    longDesc:
      'Kumagai, Nakano, Jiao et al. (2000, Limnology 1(3): 191–200, Lake Biwa Research Institute) ' +
      'demonstrated a positive feedback loop: surface cyanobacteria absorb solar radiation in the top ' +
      '0.5 m, intensifying near-surface heating, which strengthens stratification, which favours ' +
      'buoyant cyanobacteria. The model computes a Feedback Amplification Factor — a multiplier on ' +
      'the predicted cyanobacteria growth rate during stratified periods.',
    ref: 'Kumagai et al. 2000 (Lake Biwa, Japan)',
    status: 'available',
    path: '/module/kumagai-feedback',
    category: 'placement',
    iconName: 'FlaskConical',
    affects: ['Algal Dynamics — cyanobacteria growth', 'Thermal Model'],
    softConflicts: [],
  },

  // ─── Japan: NOM-driven SOD enhancement (Imai et al. 1998, NIES) ───────────
  {
    id: 'nom_sod',
    label: 'NOM-driven SOD enhancement',
    shortDesc: 'Imai 1998 humic-substance impact on sediment O₂ demand',
    longDesc:
      'Imai, Fukushima & Matsushige (1998, Wat. Sci. Tech. 37(2): 137–144, NIES Tsukuba) showed ' +
      'that humic substances (NOM) measurably increase sediment oxygen demand by providing additional ' +
      'labile carbon to heterotrophic respiration. The model multiplies baseline SOD by ' +
      '(1 + 0.12 · NOM_mg_L) up to a calibration cap, and feeds back into the bed-physics page.',
    ref: 'Imai, Fukushima & Matsushige 1998 (NIES Tsukuba, Japan)',
    status: 'available',
    path: '/module/nom-sod',
    category: 'placement',
    iconName: 'Mountain',
    affects: ['Bed Assessment §6.1', 'Algal Dynamics — DO budget'],
    softConflicts: [
      { id: 'boudreau_diagenesis',
        reason: 'Both adjust the sediment O₂ budget. NOM-SOD adds a heterotrophic respiration multiplier; ' +
                'Boudreau diagenesis solves the steady-state pore-water profile. They compound when both ' +
                'are on — review the combined SOD figure on the Bed Assessment page before downstream use.' },
    ],
  },

  // ─── China: Redox-driven P release (Ma & Wu 2008, CAS) ────────────────────
  {
    id: 'redox_p_release',
    label: 'Redox-driven sediment P release',
    shortDesc: 'Ma & Wu 2008 P flux as a function of redox potential',
    longDesc:
      'Ma & Wu (2008, Environ. Sci. Tech. 42(20): 7693–7699, Chinese Academy of Sciences) calibrated ' +
      'sediment phosphate release rate as a continuous function of redox potential rather than a ' +
      'binary aerobic/anoxic switch. P_release = P_max · (1 − tanh((ORP − ORP_50)/k_ORP)) where ' +
      'ORP_50 ≈ +100 mV and k_ORP ≈ 60 mV. Smooths the discontinuity at the redox transition and ' +
      'gives realistic intermediate P fluxes during DO recovery.',
    ref: 'Ma & Wu 2008 (Chinese Academy of Sciences)',
    status: 'available',
    path: '/module/redox-p-release',
    category: 'placement',
    iconName: 'Leaf',
    affects: ['Bed Assessment §6.3', 'Algal Dynamics — P source'],
    softConflicts: [
      { id: 'boudreau_diagenesis',
        reason: 'Both predict P release from anoxic sediment. Boudreau frames it via aerobic depth; ' +
                'Ma & Wu via redox potential. They give similar answers under steady state but Ma & Wu ' +
                'captures the smooth transition better during DO recovery.' },
    ],
  },

  // ─── China: Geosmin/2-MIB predictor (Wang et al. 2010, CAS Beijing) ───────
  {
    id: 'geosmin_predictor',
    label: 'Geosmin & 2-MIB taste-and-odour predictor',
    shortDesc: 'Wang 2010 cyanobacteria-driven T&O metabolite prediction',
    longDesc:
      'Wang, Zhang, Yang & Tao (2010, J. Water Supply Res. Tech. 59(4): 230–237, Chinese Academy of ' +
      'Sciences Beijing) calibrated geosmin and 2-MIB production rates from Anabaena and Microcystis ' +
      'biomass in Chinese drinking-water reservoirs. Concentrations above 10 ng/L trigger consumer ' +
      'complaints; > 100 ng/L breaches the unwritten DWI guideline. The model predicts both metabolites ' +
      'from chlorophyll-a, cyanobacteria fraction, and stratification status.',
    ref: 'Wang et al. 2010 (CAS Beijing) + Lin & Lin 2003 (Taiwan)',
    status: 'available',
    path: '/module/geosmin-predictor',
    category: 'placement',
    iconName: 'AlertOctagon',
    affects: ['Algal Dynamics — T&O risk', 'Sampling Plan — geosmin sample'],
    softConflicts: [],
  },

  // ─── China: Sediment capping efficacy (Chen et al. 2017, BNU) ─────────────
  {
    id: 'sediment_capping',
    label: 'Sediment capping efficacy',
    shortDesc: 'Chen 2017 in-situ capping materials for P + Microcystis mitigation',
    longDesc:
      'Chen, Lyu, Zhao et al. (2017, Water Res. 113: 132–141, Beijing Normal University) tested ' +
      'copper-modified zeolite as an in-situ sediment cap. Predicts the P-flux reduction (typically ' +
      '70-90 %) and the Microcystis suppression duration as a function of cap thickness, material ' +
      'CEC (cation exchange capacity), and overlying water chemistry. Capping is an alternative to ' +
      'aeration for very high internal-P-loading sites.',
    ref: 'Chen et al. 2017 (Beijing Normal University)',
    status: 'available',
    path: '/module/sediment-capping',
    category: 'placement',
    iconName: 'Mountain',
    affects: ['Bed Assessment §6.3', 'Validation L6 — alternative mitigation'],
    softConflicts: [
      { id: 'resusp_penalty',
        reason: 'Capping eliminates the resuspension risk by physically isolating the underlying organic ' +
                'sediment. If a cap is in place, the Bryant 2011 resuspension flag becomes moot. Use one ' +
                'or the other depending on whether capping is planned for the scheme.' },
    ],
  },

  // ─── China: N+P co-limitation diagnostic (Zhou et al. 2019, NIGLAS) ───────
  {
    id: 'np_colimitation',
    label: 'N + P co-limitation diagnostic',
    shortDesc: 'Zhou 2019 dual-nutrient limitation in eutrophic lakes',
    longDesc:
      'Zhou, Han, Brookes & Qin (2019, Environ. Pollut. 292: 118276, Nanjing Institute of Geography ' +
      'and Limnology, CAS) demonstrated that classical "P-only limitation" oversimplifies eutrophic lakes. ' +
      'For TN:TP ratios below 16 (Redfield), nitrogen often co-limits. The diagnostic computes a ' +
      'Limitation Index L = min(N/(N+K_N), P/(P+K_P)) and flags whether single-nutrient (P-only or N-only) ' +
      'or multiplicative co-limitation is the correct framework for the site.',
    ref: 'Zhou et al. 2019 (NIGLAS, Chinese Academy of Sciences)',
    status: 'available',
    path: '/module/np-colimitation',
    category: 'placement',
    iconName: 'FlaskConical',
    affects: ['Algal Dynamics — limitation logic', 'Reckhow P-retention'],
    softConflicts: [],
  },

  // ─── Wüest 1992 — Double-plume bubble model for deep stratified lakes ───
  {
    id: 'wuest_double_plume',
    label: 'Wüest double-plume bubble model',
    shortDesc: 'Detraining bubble-plume in stratified deep water',
    longDesc:
      'Wüest, Brooks & Imboden (1992, Water Resources Research 28(12): 3235-3250, EAWAG, Switzerland) ' +
      'extended the classical Asaeda-Imberger plume by allowing inner (bubble) and outer (entrained) plume ' +
      'streams to detrain water at the height of neutral buoyancy. Critical for deep lakes (>15 m) where ' +
      'plumes do not reach the surface but spread laterally at the pycnocline. Computes detrainment height, ' +
      'plume diameter at detrainment, induced volume flux Q_d, and oxygen transferred above/below the cline.',
    ref: 'Wüest, Brooks & Imboden 1992 (EAWAG, Switzerland)',
    status: 'available',
    path: '/module/wuest-plume',
    category: 'placement',
    iconName: 'Waves',
    affects: ['Plume Coverage Map', 'Validation Steps L7', 'Asaeda-Imberger C-criterion'],
    softConflicts: [
      { id: 'asaeda_c',
        reason: 'Both predict bubble-plume behaviour but in different depth regimes. Asaeda-Imberger is the ' +
                'merging-criterion shorthand for shallow plumes that reach the surface. Wüest double-plume is the ' +
                'detraining model for deep stratified lakes where the plume stops at the pycnocline. Use Wüest ' +
                'when reservoir depth > 15 m and stratification is strong; otherwise stick with Asaeda-Imberger.' },
    ],
  },

  // ─── McGinnis 2006 — Bubble dissolution & methane oxidation in plumes ────
  {
    id: 'mcginnis_bubble',
    label: 'McGinnis bubble dissolution & CH₄ oxidation',
    shortDesc: 'Mass-transfer of O₂/N₂/CH₄ in rising bubbles',
    longDesc:
      'McGinnis & Little (2002, Water Research 36(18): 4627-4635) and McGinnis et al. (2006, J. Geophys. ' +
      'Res. 111: C09007, EAWAG / Virginia Tech) coupled bubble equation-of-state with gas-side and water-side ' +
      'mass transfer to predict how much O₂ a rising bubble actually delivers (typically 40-90% over a 10-20 m ' +
      'rise) and how much CH₄ ebullition oxidises within the bubble before reaching the atmosphere. Replaces ' +
      'the flat "OTE = 12%" assumption with depth-resolved dissolution physics.',
    ref: 'McGinnis & Little 2002; McGinnis et al. 2006 (EAWAG)',
    status: 'available',
    path: '/module/mcginnis-bubble',
    category: 'placement',
    iconName: 'CircleDot',
    affects: ['Aeration Spec — OTE', 'Custom Diffuser', 'GHG Balance'],
    softConflicts: [
      { id: 'higbie_kl',
        reason: 'Higbie penetration theory provides kL for the bubble surface; McGinnis uses a full bubble ' +
                'mass-balance that already includes its own kL formulation (Frössling). The two are partly ' +
                'redundant — McGinnis supersedes Higbie when both are enabled, but no error.' },
    ],
  },

  // ─── Mehta-Partheniades — Cohesive sediment erosion/deposition kinetics ──
  {
    id: 'mehta_partheniades',
    label: 'Mehta-Partheniades cohesive sediment',
    shortDesc: 'Floc erosion/deposition under bed shear',
    longDesc:
      'Partheniades (1965, J. Hydraul. Div. ASCE 91: 105-139) and Mehta (1986, Estuarine Cohesive Sediment ' +
      'Dynamics, Springer) provide the canonical erosion-rate law E = M·(τ/τ_ce − 1) for τ > τ_ce, and the ' +
      'Krone deposition law D = w_s·C·(1 − τ/τ_cd) for τ < τ_cd. Replaces the blanket "resuspension penalty" ' +
      'with a proper bed-shear-driven rate. Aeration-induced near-bed velocity is converted to τ_b via the ' +
      'Soulsby quadratic drag law, then fed into the erosion/deposition kinetics.',
    ref: 'Partheniades 1965; Mehta 1986; Krone 1962',
    status: 'available',
    path: '/module/mehta-partheniades',
    category: 'placement',
    iconName: 'Layers',
    affects: ['Bed Assessment §4', 'Sediment Bed Physics', 'Validation L6'],
    softConflicts: [
      { id: 'resusp_penalty',
        reason: 'Bryant 2011 "resuspension penalty" is an empirical shorthand for the same physics that ' +
                'Mehta-Partheniades resolves mechanistically. When both are enabled the simulator uses ' +
                'Mehta-Partheniades (more physical) and the Bryant penalty becomes redundant. Disable one ' +
                'to keep outputs interpretable.' },
    ],
  },

  // ─── Aller 1982 — Bioturbation enhancement of porewater diffusion ────────
  {
    id: 'aller_bioturbation',
    label: 'Aller bioturbation porewater mixing',
    shortDesc: 'Biological mixing enhances sediment diffusivity',
    longDesc:
      'Aller (1982, Animal-Sediment Relations, Plenum; and 1980, Geochim. Cosmochim. Acta 44: 1955-1965) ' +
      'showed that benthic macrofauna (chironomids, oligochaetes, tubificids) homogenise the upper 5-10 cm ' +
      'of sediment, raising effective porewater diffusivity by 1-2 orders of magnitude over molecular ' +
      'diffusion. The module computes D_b (biodiffusion coefficient) from observed faunal density and ' +
      'multiplies the Boudreau molecular flux to yield realistic SOD and SRP fluxes for organic-rich beds.',
    ref: 'Aller 1980, 1982 (SUNY Stony Brook)',
    status: 'available',
    path: '/module/aller-bioturbation',
    category: 'placement',
    iconName: 'Bug',
    affects: ['Sediment Bed Physics', 'Boudreau diagenesis flux'],
    softConflicts: [
      { id: 'boudreau_diagenesis',
        reason: 'Boudreau provides the molecular diffusion baseline; Aller multiplies it by a biodiffusion ' +
                'factor. The two are designed to combine — Boudreau ON + Aller ON gives the most realistic ' +
                'flux. Soft flag only because users sometimes mistake the multiplication for double-counting.' },
    ],
  },

  // ─── Davison 1993 — Fe/Mn redox cycling in anoxic hypolimnia ─────────────
  {
    id: 'davison_fe_mn',
    label: 'Davison Fe-Mn redox cycling',
    shortDesc: 'Iron & manganese accumulation in anoxic hypolimnion',
    longDesc:
      'Davison (1993, Earth-Science Reviews 34(2): 119-163, Lancaster University) reviewed Fe(II)/Mn(II) ' +
      'cycling at the oxic-anoxic interface, providing pseudo-first-order oxidation kinetics: ' +
      'k_Fe ≈ 1.5×10¹³ [O₂][OH⁻]² and k_Mn ≈ 7×10¹¹ [O₂][OH⁻]². Below the redoxcline, dissolved Fe(II) and ' +
      'Mn(II) accumulate to mg/L levels and discolour water at overturn. The module tracks accumulation, ' +
      'predicts breakthrough timing, and flags WTW Mn risk against the UK DWI 50 µg/L limit.',
    ref: 'Davison 1993 (Lancaster); Stumm & Morgan 1996',
    status: 'available',
    path: '/module/davison-fe-mn',
    category: 'placement',
    iconName: 'Magnet',
    affects: ['Algal Dynamics — Fe limitation', 'Compliance — Mn WTW risk'],
    softConflicts: [
      { id: 'redox_p_release',
        reason: 'Both modules describe ORP-driven internal loading; this module tracks Fe & Mn, the other ' +
                'tracks P. They are physically coupled (the same Fe(III)→Fe(II) reduction releases both ' +
                'sorbed P and Fe(II)). When both are enabled the diagnostics tell a consistent story but ' +
                'consider whether the user really needs both tabs or one combined view.' },
    ],
  },

  // ─── MacIntyre 1993 — Nocturnal convective mixing ────────────────────────
  {
    id: 'macintyre_convection',
    label: 'MacIntyre nocturnal convective mixing',
    shortDesc: 'Night cooling drives surface-layer turbulence',
    longDesc:
      'MacIntyre (1993, Limnol. Oceanogr. 38(4): 798-817, UC Santa Barbara) demonstrated that night-time ' +
      'surface cooling generates penetrative convection in the surface mixed layer, with TKE dissipation ' +
      'rates ε ≈ 10⁻⁶-10⁻⁵ W/kg comparable to or exceeding daytime wind-shear turbulence in calm conditions. ' +
      'The module computes buoyancy flux B₀ from net surface heat loss Q_n and adds the convective mixing ' +
      'velocity w* = (B₀·h)^(1/3) to the wind-shear mixing budget. Important for calm summer nights when ' +
      'wind-only models under-predict mixing depth.',
    ref: 'MacIntyre 1993; Imberger 1985',
    status: 'available',
    path: '/module/macintyre-convection',
    category: 'placement',
    iconName: 'Moon',
    affects: ['Thermal Model', 'Wind-tilt correction'],
    softConflicts: [],
  },

  // ─── Reservoir physics — Reckhow phosphorus retention (1979) ──────────────
  {
    id: 'reckhow_p_retention',
    label: 'Reckhow P-retention model',
    shortDesc: 'Alternative to Vollenweider for in-lake [TP]',
    longDesc:
      'Reckhow 1979 empirical formula for in-lake total phosphorus from areal loading and hydraulic ' +
      'residence time: [P]_lake = L_P / (q_s + 11.6 + 1.2·q_s). Refinement of Vollenweider 1976 ' +
      'using a larger calibration set of US lakes. Useful sensitivity check on the standard ' +
      'Vollenweider prediction used elsewhere in the tool. Adds a row to the algae page comparing ' +
      'both predictions.',
    ref: 'Reckhow 1979; Dillon & Rigler 1974',
    status: 'available',
    path: '/module/reckhow-p',
    category: 'placement',
    iconName: 'Leaf',
    affects: ['Algal Dynamics — P loading cross-check', 'GHG Balance'],
    softConflicts: [],
  },

  {
    id: 'langmuir',
    softConflicts: [
      { id: 'asaeda_c',
        reason: 'Langmuir cells govern surface mixing (top 0.5-1 m). Asaeda-Imberger describes plume ' +
                'detrainment in the stratified hypolimnion. The two regimes co-exist in a real reservoir but ' +
                'they describe different physical layers — do not use Langmuir efficiency to override the ' +
                'C-criterion verdict, or vice versa.' },
    ],
    label: 'Langmuir circulation correction',
    shortDesc: 'Wind-driven helical convergence cells',
    longDesc:
      'Thorpe 2004 Langmuir circulation theory. Modules placed across the prevailing wind ' +
      'are drawn into convergence zones — useful for surface boil distribution but bad ' +
      'for hypolimnion mixing. Adds an orientation-dependent efficiency factor to each ' +
      'placement candidate.',
    ref: 'Thorpe 2004; Plueddemann & Weller 1999',
    status: 'available',
    path: '/module/langmuir',
    category: 'placement',
    iconName: 'Wind',
    affects: ['Plume Coverage'],
  },

  // ─── Seasonal Adaptive Aeration Scheduler (year-round orchestrator) ──────
  {
    id: 'seasonal_scheduler',
    label: 'Seasonal Adaptive Aeration Scheduler',
    shortDesc: 'Year-round regime switching driven by Lake Number / stratification state',
    longDesc:
      'Orchestrator module that selects which aeration regime to run based on the current Lake Number (LN), ' +
      'stratification stability, surface temperature and chl-a / cyano fraction. Outputs one of four ' +
      'regime tags: OFF (winter, fully mixed), LOW (shoulder, conditioning), TEM (summer, epilimnetic) ' +
      'or FULL (rescue, anoxic-bed emergency). Does not replace any other module - sits above them as a ' +
      'control-system layer, switching the active modules in/out of the simulation across the seasonal ' +
      'cycle. Synthesises the Cooke 2005 management framework, the Antenucci-Imberger 2000 Lake-Number ' +
      'criterion, the Beutel-Horne 1999 seasonal hypolimnetic-oxygenation envelope and the Gachter-Wehrli ' +
      '1998 long-term Sempach evidence base. Designed for shallow polymictic UK reservoirs where natural ' +
      'mixing dominates 6-9 months of the year and aeration is only needed for the 3-6 month summer ' +
      'cyano-risk window. UK compliance: WFD Article 11 supplementary measure, DWI Reg 4(2)(a) source ' +
      'control, DEFRA cyano guidance, Reservoirs Act 1975 (no civil-engineering implications - control ' +
      'logic only).',
    ref: 'Cooke, Welch, Peterson & Nichols 2005 (Restoration & Management of Lakes & Reservoirs, 3rd ed., CRC); Antenucci, Imberger & Heinz 2000 (Limnology & Oceanography 45(7)); Beutel & Horne 1999; Gachter & Wehrli 1998 (Lake Sempach); Hipsey, Bruce et al. 2017 (GLM-AED2)',
    status: 'available',
    path: '/module/seasonal-scheduler',
    category: 'optimisation',
    iconName: 'CalendarClock',
    affects: ['All operational modules - switches the active set per season', 'Aeration Knowledge Base', 'O&M Schedule'],
    softConflicts: [],
  },

  // ─── Pre-emptive Whole-Column Mixing (NL / UK shallow lakes evidence) ──
  {
    id: 'pwcm_visser',
    label: 'Pre-emptive Whole-Column Mixing (PWCM)',
    shortDesc: 'Visser-Verspagen 1996 / CIRIA C753 - shallow polymictic UK reservoir summer regime',
    longDesc:
      'Pre-emptive Whole-Column Mixing (PWCM) for shallow polymictic UK lowland reservoirs ' +
      '(mean depth < 5 m). Operates the existing diffuser array at ~ 65 % of FULL Q_air during ' +
      'cyanobacterial bloom-window events to achieve whole-column overturn within 12-24 hours, ' +
      'breaking up Microcystis surface scum and disrupting Anabaena buoyancy regulation. ' +
      'Developed and operationally validated at Lake Nieuwe Meer (Netherlands, 4.6 m mean depth, ' +
      'eutrophic, Microcystis-dominated) with 25+ years of evidence showing 80-95 % cyano ' +
      'biomass reduction. Cross-validated at Loch Leven (Scotland, 3.9 m mean depth) and ' +
      'documented in UK CIRIA C753 (2017) reservoir aeration design guidance. Unlike Patterson ' +
      '1984 TEM (which targets the surface mixed layer in deep stratified reservoirs to preserve ' +
      'a cold hypolimnion), PWCM is designed for shallow basins where no meaningful hypolimnion ' +
      'exists and the engineering objective is full-basin overturn during the cyano risk window. ' +
      'Default regime style for any site with mean depth < 5 m; TEM remains available for deeper ' +
      'stratified sites (mean depth >= 5 m).',
    ref: 'Visser, Ibelings, Van der Veer, Koedood & Mur 1996 Freshwater Biology 36: 435-450; ' +
         'Verspagen, Snelder, Visser, Joehnk, Ibelings, Mur & Huisman 2006 Freshwater Biology 51: 952-969; ' +
         'CIRIA C753 (2017) Reservoir Aeration Design Guidance; Carvalho et al. 2013 Hydrobiologia 710 (Loch Leven); ' +
         'Cooke, Welch, Peterson & Nichols 2005 ch.14 (Shallow Lake Management)',
    status: 'available',
    path: '/module/pwcm-visser',
    category: 'placement',
    iconName: 'Waves',
    affects: ['Thermal Model - whole-column mixing during bloom windows',
              'Algal Dynamics - Microcystis / Anabaena suppression',
              'Plume Coverage - whole-basin regime',
              'Aeration Knowledge Base'],
    softConflicts: [
      { id: 'tem_epilimnetic',
        reason: 'PWCM (Visser-Verspagen) and TEM (Patterson 1984) are two strategies for the same ' +
                'problem - cyano control in stratified summer windows - but designed for different ' +
                'depth regimes. PWCM = whole-column mixing for shallow polymictic (< 5 m) basins; ' +
                'TEM = SML-confined plume for deep stratified (>= 5 m) reservoirs. Auto-selected ' +
                'in site spec based on mean depth; users may toggle manually if site characteristics ' +
                'are borderline. Both modules cannot be active simultaneously in the same regime.' },
    ],
  },

  // ─── Targeted Epilimnetic Mixing (Australian research, summer-warm-water) ──
  {
    id: 'tem_epilimnetic',
    label: 'Targeted Epilimnetic Mixing (TEM)',
    shortDesc: 'Surface-confined aeration regime for warm-water summer suppression',
    longDesc:
      'Patterson 1984 / Hamilton & Schladow 1997 / Brookes & Carey 2011 epilimnion-only mixing regime, ' +
      'developed at the UWA Centre for Water Research and demonstrated at Chaffey Dam (NSW), Cotter Dam ' +
      '(ACT), Sugarloaf Reservoir (Vic) and Lake Burragorang (NSW). Operates the existing diffuser array ' +
      'at reduced Q_air so the bubble plume only mixes the surface warm layer where buoyant Microcystis ' +
      'accumulate — disrupting their vertical migration cycle without mixing the cold bottom water that ' +
      'the WTW abstraction relies on. Energy demand is 5-15 % of full destratification, making it ideal ' +
      'for summer windows when stratification is weak but cyano risk is high.  In the train, TEM acts as ' +
      'an ALTERNATE OPERATING REGIME for the same hardware (McGinnis / Wuest plume models are re-cast at ' +
      'the lower airflow), and is synergistic with the Kumagai cyano-stratification feedback module — TEM ' +
      'directly breaks the very feedback loop Kumagai describes. UK compliance: WFD Article 11 ' +
      'supplementary measure; DWI Reg 4(2)(a) "wholesomeness" at source; DEFRA cyanobacterial-risk ' +
      'guidance mixing recommendation; no Reservoirs Act 1975 Section 10 implications (no civil works).',
    ref: 'Patterson, Hamblin & Imberger 1984; Hamilton & Schladow 1997; Brookes & Carey 2011',
    status: 'available',
    path: '/module/tem-epilimnetic',
    category: 'placement',
    iconName: 'Waves',
    affects: ['Thermal Model — surface mixed layer depth', 'Algal Dynamics — cyano suppression', 'Plume Coverage — epilimnion regime', 'Aeration Knowledge Base'],
    softConflicts: [
      { id: 'deep_zone',
        reason: 'Deep-zone placement prioritises diffusers in the deepest 30 % of the basin, ' +
                'optimised for full destratification. TEM uses the same hardware but at reduced ' +
                'airflow so the plume is surface-confined. Both can co-exist physically — the ' +
                'deep-zone diffusers run at lower Q_air during TEM windows — but the energy ' +
                'budget and OTE accounting must be re-cast for the shallow-plume regime. ' +
                'Document which regime is active when both modules are on.' },
      { id: 'mcginnis_bubble',
        reason: 'McGinnis 2006 single-bubble model assumes full-column transit. Under TEM the ' +
                'plume terminates at the surface-mixed-layer base. OTE is re-calibrated for the ' +
                'shorter rise path (0.5-2 m instead of full depth). The McGinnis kLa formulation ' +
                'still applies, but the integration limit changes.' },
    ],
  },
];

export interface ModulesState {
  enabled: Record<string, boolean>;
}

const DEFAULT_STATE: ModulesState = { enabled: {} };

export interface BlockInfo { blockedBy: string; reason: string; }
export interface SoftWarning { withId: string; withLabel: string; reason: string; }

/** Hook returning current module-enable state and a toggle function. */
export function useModulesEnabled(): {
  enabled: Record<string, boolean>;
  toggle: (id: string) => void;
  setEnabled: (id: string, v: boolean) => void;
  isEnabled: (id: string) => boolean;
  enabledList: OptModule[];
  enabledCount: number;
  /** Returns block info if a hard conflict prevents enabling this module, else null. */
  blockInfo: (id: string) => BlockInfo | null;
  /** Returns active soft warnings for this module (regardless of enabled state). */
  softWarnings: (id: string) => SoftWarning[];
} {
  const [state, setState] = useState<ModulesState>(
    () => loadPersisted<ModulesState>(MODULES_KEY, DEFAULT_STATE)
  );

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === MODULES_KEY) {
        setState(loadPersisted<ModulesState>(MODULES_KEY, DEFAULT_STATE));
      }
    });
    return () => unsub();
  }, []);

  const findById = (id: string) => MODULES.find(m => m.id === id);

  /** Identify hard conflict blocking module `id` from being enabled. */
  const blockInfo = (id: string): BlockInfo | null => {
    const m = findById(id);
    if (!m?.hardConflicts) return null;
    for (const conflict of m.hardConflicts) {
      if (state.enabled[conflict.id]) {
        const other = findById(conflict.id);
        return { blockedBy: other?.label ?? conflict.id, reason: conflict.reason };
      }
    }
    return null;
  };

  /** Active soft warnings for this module (both directions). */
  const softWarnings = (id: string): SoftWarning[] => {
    const m = findById(id);
    if (!m) return [];
    const out: SoftWarning[] = [];
    // 1) this module has softConflicts pointing to others — show if other is enabled
    (m.softConflicts ?? []).forEach(c => {
      if (state.enabled[c.id]) {
        const other = findById(c.id);
        out.push({ withId: c.id, withLabel: other?.label ?? c.id, reason: c.reason });
      }
    });
    // 2) other modules have softConflicts pointing here — show if that other is enabled
    MODULES.forEach(other => {
      if (other.id === id || !state.enabled[other.id]) return;
      (other.softConflicts ?? []).forEach(c => {
        if (c.id === id && !out.find(w => w.withId === other.id)) {
          out.push({ withId: other.id, withLabel: other.label, reason: c.reason });
        }
      });
    });
    return out;
  };

  const setEnabled = (id: string, v: boolean) => {
    // Block hard-conflict enables
    if (v && blockInfo(id)) return;
    const next = { enabled: { ...state.enabled, [id]: v } };
    savePersisted(MODULES_KEY, next);
    setState(next);
  };
  const toggle = (id: string) => setEnabled(id, !state.enabled[id]);
  const isEnabled = (id: string): boolean => Boolean(state.enabled[id]);
  const enabledList = MODULES.filter(m => state.enabled[m.id]);

  return {
    enabled: state.enabled, toggle, setEnabled, isEnabled, enabledList, enabledCount: enabledList.length,
    blockInfo, softWarnings,
  };
}

/** Read modules state synchronously (for non-React contexts). */
export function readModulesEnabled(): Record<string, boolean> {
  return loadPersisted<ModulesState>(MODULES_KEY, DEFAULT_STATE).enabled;
}
